import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { UNDO_CAPTURE } from '@awork/_shared/services/undo/undo-http-context'
import { Injectable, Injector, inject, type Signal } from '@angular/core'
import type { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http'
import { HttpHeaders } from '@angular/common/http'
import { environment, apiEndpoint, version } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { AccountQuery } from '@awork/_shared/state/account.query'
import type { AccountState } from '@awork/_shared/models/account.model'
import { guidRegexString } from '@awork/_shared/static-data/regular-expressions'
import getHostname from '@awork/_shared/functions/get-hostname'

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private injector = inject(Injector)

  private accountQuery: AccountQuery
  private signalService: SignalService

  private isUndoEnabled: Signal<boolean>

  /**
   * Adds API request headers and web credentials.
   * @param {HttpRequest<unknown>} req - The outgoing request.
   * @param {HttpHandler} next - The next handler.
   * @returns {Observable<HttpEvent<unknown>>} The response events.
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.accountQuery = this.injector.get(AccountQuery)
    this.signalService = this.injector.get(SignalService)
    this.isUndoEnabled ??= this.injector.get(FeatureFlagQuery).queryFlag(FeatureFlag.undoActions)

    const apiEntry = '/api/v1'
    const requestApiUrl = getHostname(req.url) + apiEntry
    const apiUrl = getHostname(apiEndpoint) + apiEntry

    if (req.url.includes(apiEndpoint) || requestApiUrl === apiUrl) {
      // Default request options
      const options = {
        withCredentials: true
      }

      if (req.headers?.has('credentials')) {
        // Remove custom credentials header to avoid issues with CORS preflight
        options['withCredentials'] = req.headers.get('credentials') !== 'omit'
        req.headers.delete('credentials')
      }

      let headers = req.headers ? req.headers : new HttpHeaders()

      // If the request is 'cookie' or 'token' (login), set the required header
      if (req.url.endsWith('cookie') || req.url.endsWith('token')) {
        headers = headers.set('Content-Type', 'application/x-www-form-urlencoded')
      }

      if (!this.isExcluded(req) && this.signalService.connectionId !== null) {
        headers = headers.set('aw-websocket-connection-id', this.signalService.connectionId)
      }

      headers = this.addUndoHeaders(req, headers)

      // If in dev mode, some headers need to be added.
      if (environment === 'local') {
        const account: AccountState = this.accountQuery.getAccount()

        // In all other cases (excluding 'refreshtoken' and 'cookie' endpoint),
        // the access token needs to be added because we do not have a cookie.
        if (
          account.accessToken !== null &&
          !req.url.endsWith('refreshtoken') &&
          !req.url.endsWith('cookie') &&
          !req.url.startsWith('https://blob.') &&
          !req.url.startsWith('https://upload.cwork.io')
        ) {
          const accessToken = account.accessToken

          headers = headers.set('Authorization', `Bearer ${accessToken}`)
        }

        // To get the access token in the response of the login endpoint
        headers = headers.set('aw-mobile', 'true')
      }

      // Set default headers
      headers = headers.set('accept', 'application/json')
      headers = headers.set('aw-version', version)

      options['headers'] = headers

      // Clone the request to add the new options
      const newReq = req.clone(options)

      // Pass on the cloned request instead of the original request.
      return next.handle(newReq)
    } else {
      return next.handle(req)
    }
  }

  /**
   * Adds the capture contract for API writes when Undo is enabled.
   * @param {HttpRequest<unknown>} req - The outgoing API request.
   * @param {HttpHeaders} headers - The existing request headers.
   * @returns {HttpHeaders} Headers with the requested capture mode.
   */
  private addUndoHeaders(req: HttpRequest<unknown>, headers: HttpHeaders): HttpHeaders {
    const isApiRequest = req.url === apiEndpoint || req.url.startsWith(`${apiEndpoint}/`)

    if (!isApiRequest || !this.isUndoEnabled() || !['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)) {
      return headers
    }

    const capture = req.context.get(UNDO_CAPTURE)

    return headers.set('aw-undo-capture', capture === 'skip' ? 'skip' : '1')
  }

  /**
   * Determines if the call needs to be excluded from sending the websocket connection id.
   * When the websocket connection id is not sent, then we can receive websockets of own user
   * @param {HttpRequest<unknown>} req
   * @returns {boolean} Whether own websocket events are needed.
   */
  isExcluded(req: HttpRequest<unknown>): boolean {
    const excludedCalls = [
      {
        url: 'addtaskbundle',
        method: 'POST',
        checkForEndsWith: true
      },
      {
        url: 'projects',
        method: 'POST',
        checkForEndsWith: true
      },
      {
        url: 'addProjects',
        method: 'POST',
        checkForEndsWith: true
      },
      {
        url: `tasklists/${guidRegexString}/copy`,
        method: 'POST',
        checkForEndsWith: true
      },
      {
        url: 'batch/copy',
        method: 'POST',
        checkForEndsWith: true
      },
      {
        url: `tasks/${guidRegexString}/copy`,
        method: 'POST',
        checkForEndsWith: true
      },
      {
        url: `projects/${guidRegexString}/copy`,
        method: 'POST',
        checkForEndsWith: true
      }
    ]
    return excludedCalls.some(excludedCall => {
      const excludedURL = excludedCall.checkForEndsWith ? excludedCall.url + '$' : excludedCall.url
      const regex = new RegExp(excludedURL)

      return req.method === excludedCall.method && regex.test(req.url)
    })
  }
}
